import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Card, Empty, Image, Popconfirm, Spin, Tabs, Upload, message } from 'antd';
import { DeleteOutlined, PictureOutlined, UploadOutlined } from '@ant-design/icons';
import { addOrderMedia, listOrderMedia, removeOrderMedia, uploadSharedFile } from '../modules/orders/api';

/**
 * OVO-103 — dispatcher-side media upload + browse, scoped per step
 * (pickup / delivery / service). One Tab per step. Upload pipeline:
 *
 *   1. User picks a file (any image)
 *   2. We POST it to /common/shared-upload to get back a fileId + url
 *   3. We POST /orders/:orderCode/media { fileId, stepType } to link it
 *   4. Re-fetch the media list
 *
 * Deletes only unlink the FileOrder row (the underlying File may be reused
 * by other orders — backend protects POD-type links from being deleted
 * through this endpoint).
 *
 * Hidden when `viewOnly` (partner viewers see the driver-app surfaces, not
 * the dispatcher upload UI).
 */
export default function OrderMediaSection({ orderCode, viewOnly = false }) {
  const host = useSelector((s) => s.auth?.user?.host);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingStep, setUploadingStep] = useState(null);

  const refresh = useCallback(async () => {
    if (!orderCode) return;
    setLoading(true);
    const { response, error } = await listOrderMedia(orderCode);
    if (error) {
      message.error(error.message || 'Failed to load media');
    } else if (Array.isArray(response)) {
      setMedia(response);
    }
    setLoading(false);
  }, [orderCode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const groups = { pickup: [], delivery: [], service: [] };
    for (const m of media) {
      if (m.type === 'PICKUP_HINT') groups.pickup.push(m);
      else if (m.type === 'DELIVERY_HINT') groups.delivery.push(m);
      else if (m.type === 'SERVICE_HINT') groups.service.push(m);
    }
    return groups;
  }, [media]);

  const handleUpload = async (stepType, file) => {
    setUploadingStep(stepType);
    try {
      const { response: uploaded, error: uploadErr } = await uploadSharedFile(file);
      if (uploadErr || !uploaded?.id) {
        message.error(uploadErr?.message || 'Upload failed');
        return false;
      }
      const { response: linked, error: linkErr } = await addOrderMedia(orderCode, {
        fileId: uploaded.id,
        stepType,
      });
      if (linkErr || !linked) {
        message.error(linkErr?.message || 'Could not attach file to order');
        return false;
      }
      message.success(`Photo added to ${stepType}`);
      await refresh();
      return false; // Tell AntD Upload to NOT do its own request
    } catch (e) {
      message.error(e?.message || 'Upload failed');
      return false;
    } finally {
      setUploadingStep(null);
    }
  };

  const handleDelete = async (fileOrderId) => {
    const { error } = await removeOrderMedia(orderCode, fileOrderId);
    if (error) {
      message.error(error.message || 'Could not delete');
      return;
    }
    message.success('Photo removed');
    refresh();
  };

  const renderTab = (stepType, label) => {
    const items = grouped[stepType] || [];
    return {
      key: stepType,
      label: `${label} (${items.length})`,
      children: (
        <div>
          {!viewOnly && (
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => handleUpload(stepType, file)}
              disabled={uploadingStep === stepType}
            >
              <Button icon={<UploadOutlined />} loading={uploadingStep === stepType} style={{ marginBottom: 12 }}>
                Upload photo for {stepType}
              </Button>
            </Upload>
          )}
          {items.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No ${stepType} photos`}
              style={{ paddingTop: 8, paddingBottom: 8 }}
            />
          ) : (
            <Image.PreviewGroup>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {items.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      position: 'relative',
                      width: 120,
                      height: 120,
                      borderRadius: 6,
                      overflow: 'hidden',
                      border: '1px solid #eee',
                    }}
                  >
                    {host && m.file?.key ? (
                      <Image
                        src={`${host}/${m.file.key}`}
                        alt={m.type}
                        width={120}
                        height={120}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : null}
                    {!viewOnly && (
                      <Popconfirm
                        title="Remove this photo?"
                        description="The photo file stays on storage; only this attachment is removed."
                        onConfirm={() => handleDelete(m.id)}
                        okText="Remove"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            zIndex: 2,
                            opacity: 0.92,
                          }}
                        />
                      </Popconfirm>
                    )}
                  </div>
                ))}
              </div>
            </Image.PreviewGroup>
          )}
        </div>
      ),
    };
  };

  return (
    <Card
      size="small"
      title={
        <span>
          <PictureOutlined /> Photos for the driver
        </span>
      }
      style={{ marginTop: 16 }}
    >
      <Spin spinning={loading}>
        <Tabs
          items={[renderTab('pickup', 'Pickup'), renderTab('delivery', 'Delivery'), renderTab('service', 'Service')]}
        />
      </Spin>
    </Card>
  );
}
